import React, { useEffect, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Paper, Portal, SvgIcon, Switch, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import InfoIcon from '@material-ui/icons/InfoOutlined'
import { makeStyles } from '@material-ui/styles'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { matchRoutes } from 'react-router-config'
import routes from '../routes'
import configuration from '../configuration'
import { CLARITY_CONSENT_KEY, readClarityConsent, reloadAfterClarityRevocation, saveClarityConsent, syncClarityConsent } from '../utils/clarityConsent'

const useStyles = makeStyles(theme => ({
  banner: {
    '&&': {
      position: 'fixed',
      bottom: theme.spacing(3),
      left: theme.spacing(3),
      width: 470,
      maxWidth: 'calc(100vw - 48px)',
      boxSizing: 'border-box',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: theme.spacing(3),
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: '#FFFFFF',
      border: '1px solid #E7EAF0',
      borderRadius: 18,
      boxShadow: '0 20px 50px rgba(31, 45, 61, 0.10), 0 2px 8px rgba(31, 45, 61, 0.04)',
      maxHeight: 'calc(100vh - 48px)',
      overflowY: 'auto',
      '& .MuiTypography-root, & .MuiButton-root': { fontFamily: 'inherit' },
      '@media (max-width: 520px)': {
        left: 12,
        right: 12,
        bottom: 12,
        width: 'auto',
        maxWidth: 'none',
        maxHeight: 'calc(100vh - 24px)',
        padding: 20
      }
    }
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14
  },
  icon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: '#F9F3FB',
    color: '#69258C',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 21 }
  },
  heading: { flex: 1, minWidth: 0 },
  eyebrow: {
    '&&': {
      fontSize: 11,
      fontWeight: 700,
      color: '#75808C',
      letterSpacing: '0.075em',
      textTransform: 'uppercase',
      lineHeight: 'normal',
      margin: '1px 0 5px'
    }
  },
  // La specificità locale mantiene la gerarchia anche quando MUI inietta il tema dopo il componente.
  title: { '&&': { fontSize: 20, fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.3px' } },
  bannerTitle: { '&&': { fontSize: 18, fontWeight: 700, color: '#212934', letterSpacing: '-0.015em', textWrap: 'balance' } },
  close: {
    '&&': {
      width: 32,
      height: 32,
      flexShrink: 0,
      padding: 0,
      marginTop: -3,
      marginRight: -5,
      borderRadius: 8,
      color: '#71808D',
      '&:hover': { backgroundColor: '#F5F6F8', color: '#28323D' },
      '& span': { fontSize: 22, fontWeight: 400, lineHeight: 1 },
      '& svg': { fontSize: 18 }
    }
  },
  copy: { '&&': { fontSize: 13, lineHeight: 1.65, color: theme.palette.text.secondary } },
  bannerCopy: {
    '&&': {
      marginTop: 18,
      fontSize: 14,
      letterSpacing: 'normal',
      color: '#556575',
      '& strong': { color: '#374553', fontWeight: 600 }
    }
  },
  bannerNote: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    marginTop: 13,
    padding: '8px 10px',
    backgroundColor: '#F8F9FB',
    borderRadius: 9,
    color: '#687786',
    '& svg': { flexShrink: 0, fontSize: 15, marginTop: 1 },
    '& p': { fontSize: 12, lineHeight: 1.4, letterSpacing: 'normal', color: 'inherit' }
  },
  button: {
    '&&': {
      minHeight: 42,
      borderRadius: 9,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: 'none',
      boxShadow: 'none',
      padding: theme.spacing(1.25, 2),
      '&.Mui-focusVisible': { outline: '2px solid #69258C', outlineOffset: 2 }
    }
  },
  choice: { '&&': { borderColor: '#CBD5E1', color: theme.palette.text.primary } },
  bannerButton: {
    '&&': {
      minHeight: 44,
      height: 44,
      padding: '0 8px',
      fontWeight: 650,
      transition: 'transform .1s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease',
      '&:active': { transform: 'translateY(1px)' }
    }
  },
  bannerSecondary: {
    '&&': {
      backgroundColor: '#FFFFFF',
      borderColor: '#D5DCE4',
      color: '#394652',
      '&:hover': { backgroundColor: '#F8F9FB', borderColor: '#C5CED8' }
    }
  },
  primary: {
    '&&:not(.Mui-disabled)': {
      border: '1px solid #63228A',
      background: 'linear-gradient(135deg, #752A94 0%, #582080 100%)',
      color: '#FFFFFF',
      boxShadow: '0 5px 13px rgba(94, 33, 132, .17)',
      '&:hover': { boxShadow: '0 7px 18px rgba(94, 33, 132, .24)' }
    }
  },
  actions: {
    // Il CTA conserva il testo completo su una riga; su mobile entrambe le scelte occupano tutta la larghezza.
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)',
    gap: 10,
    marginTop: 20,
    '@media (max-width: 520px)': {
      gridTemplateColumns: '1fr',
      '& > :last-child': { order: -1 }
    }
  },
  bannerFooter: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: 16
  },
  customize: {
    '&&': {
      padding: 0,
      minWidth: 0,
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 550,
      lineHeight: 1.5,
      color: '#596B7C',
      '&:hover': { backgroundColor: 'transparent', color: '#63228A', textDecoration: 'underline' },
      '&.Mui-focusVisible': { outline: '2px solid #69258C', outlineOffset: 3 }
    }
  },
  preferences: {
    '&&': {
      textTransform: 'none', fontSize: 11, fontWeight: 500, color: theme.palette.text.secondary,
      position: 'fixed',
      bottom: theme.spacing(1),
      left: theme.spacing(1),
      zIndex: theme.zIndex.drawer + 1,
      borderRadius: 8,
      borderColor: theme.palette.divider,
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
      backgroundColor: theme.palette.background.paper,
      '&:hover': { backgroundColor: theme.palette.background.paper }
    }
  },
  dialog: {
    // Le dimensioni locali evitano un modal largo con la tipografia ridotta del tema globale.
    '&&': {
      width: 480,
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100% - 32px)',
      margin: 16,
      borderRadius: 16,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 24px 72px rgba(15, 23, 42, 0.18)'
    }
  },
  dialogHeader: { '&&': { padding: theme.spacing(3, 3, 0), [theme.breakpoints.down('xs')]: { padding: theme.spacing(2.5, 2.5, 0) } } },
  dialogBody: {
    '&&': {
      padding: theme.spacing(2, 3, 2.5),
      [theme.breakpoints.down('xs')]: { padding: theme.spacing(2, 2.5, 2.5) }
    }
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: '#F8FAFC',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12
  },
  optionCopy: { flex: 1, minWidth: 0 },
  optionTitle: { '&&': { fontSize: 14, fontWeight: 600, lineHeight: 1.4, cursor: 'pointer' } },
  optional: { '&&': { fontSize: 11, lineHeight: 1.5, color: theme.palette.text.secondary, marginTop: 2 } },
  optionDescription: { '&&': { marginTop: theme.spacing(1), fontSize: 12, lineHeight: 1.6 } },
  switch: {
    '&&': { width: 46, height: 28, padding: 0 },
    flexShrink: 0,
    '&& .MuiSwitch-switchBase': {
      padding: 4,
      color: '#FFF',
      '&.Mui-checked': {
        transform: 'translateX(18px)',
        color: '#FFF',
        '& + .MuiSwitch-track': { opacity: 1, backgroundColor: '#69258C' }
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': { outline: '2px solid #69258C', outlineOffset: 2 }
    },
    '&& .MuiSwitch-thumb': { width: 20, height: 20, boxShadow: '0 1px 3px rgba(15, 23, 42, 0.2)' },
    '&& .MuiSwitch-track': { borderRadius: 14, opacity: 1, backgroundColor: '#CBD5E1' }
  },
  notice: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 9,
    backgroundColor: '#FFF8ED',
    '& svg': { fontSize: 17, color: '#8F6124', marginTop: 2 },
    '& p': { fontSize: 12, lineHeight: 1.55, color: '#8F6124' }
  },
  error: { '&&': { fontSize: 12, lineHeight: 1.55, marginTop: theme.spacing(1.5) } },
  dialogActions: {
    '&&': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.6fr)',
      gap: theme.spacing(1),
      padding: theme.spacing(2, 3),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: '#FAFBFC',
      '& > *': { margin: 0 },
      [theme.breakpoints.down('xs')]: { padding: theme.spacing(2, 2.5) }
    }
  },
  privacy: {
    '&&': {
      fontFamily: theme.typography.fontFamily,
      fontSize: 12,
      fontWeight: 550,
      lineHeight: 1.5,
      color: '#596B7C',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      '& span': { fontSize: 11 },
      '&:hover': { color: '#63228A', textDecoration: 'underline' },
      '&:focus-visible': { outline: '2px solid #69258C', outlineOffset: 3 }
    }
  },
  dialogPrivacy: { marginTop: theme.spacing(2) }
}))

function PrivacyIcon () {
  return (
    <SvgIcon aria-hidden='true' viewBox='0 0 24 24'>
      <path d='M12 3l7 3v5c0 4.6-2.8 8.4-7 10-4.2-1.6-7-5.4-7-10V6l7-3z' fill='none' stroke='currentColor' strokeWidth='1.8' />
      <path d='M9 12l2 2 4-4' fill='none' stroke='currentColor' strokeWidth='1.8' />
    </SvgIcon>
  )
}

function Clarity () {
  // Le preferenze sono globali; i metadati delle route mantengono l'installazione
  // nei quattro layout originari, senza attivarla su nuove pagine come il reset password.
  const classes = useStyles()
  // In questa app ogni sezione di common.json è un namespace i18next distinto.
  const { t } = useTranslation('clarityConsent')
  const { pathname } = useLocation()
  const canInitialize = matchRoutes(routes, pathname).some(match => match.route.clarity === true)
  const [consent, setConsent] = useState(readClarityConsent)
  const [open, setOpen] = useState(false)
  const [bannerOpen, setBannerOpen] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [storageError, setStorageError] = useState(false)

  useEffect(() => {
    // In anteprima proviamo le scelte nel browser senza scaricare lo script Microsoft.
    if (configuration.clarityPreview) return
    if (syncClarityConsent(consent, canInitialize)) reloadAfterClarityRevocation()
  }, [consent, canInitialize])

  useEffect(() => {
    // Le altre schede devono rispettare revoche e cancellazione delle preferenze.
    const refresh = event => {
      if (event.key === CLARITY_CONSENT_KEY || event.key === null) {
        setConsent(readClarityConsent())
        setOpen(false)
        setBannerOpen(false)
      }
    }
    window.addEventListener('storage', refresh)
    // I timer del browser accettano al massimo circa 24 giorni; ricontrolliamo
    // la scelta fino alla scadenza, anche se la dashboard resta aperta a lungo.
    const timer = consent && window.setTimeout(() => setConsent(readClarityConsent()),
      Math.min(Math.max(consent.expiresAt - Date.now(), 0), 2147483647))
    return () => {
      window.removeEventListener('storage', refresh)
      if (timer) window.clearTimeout(timer)
    }
  }, [consent])

  const choose = value => {
    const saved = saveClarityConsent(value)
    // Senza salvataggio riuscito non abilitiamo il tracciamento né fingiamo una revoca.
    setStorageError(!saved)
    if (!saved) return
    setConsent(saved)
    setOpen(false)
    setBannerOpen(false)
  }

  const showBanner = () => {
    // Riaprire il banner non cancella il consenso salvato: il tracker cambia solo dopo una nuova scelta.
    setStorageError(false)
    setBannerOpen(true)
  }

  const showPreferences = () => {
    setAnalytics(Boolean(consent && consent.analytics))
    setStorageError(false)
    setOpen(true)
  }

  const privacyLink = (
    <Link className={classes.privacy} href='https://www.iubenda.com/privacy-policy/99868774' target='_blank' rel='noopener noreferrer'>
      {t('privacy')}
      <span aria-hidden='true'>↗</span>
    </Link>
  )
  const errorMessage = storageError && (
    <Typography className={classes.error} color='error' role='alert' variant='body2'>{t('storageError')}</Typography>
  )
  const revoking = Boolean(consent && consent.analytics && !analytics)

  return (
    <>
      <Portal>
        {(!consent || bannerOpen) && !open ? (
          <Paper className={classes.banner} role='region' aria-labelledby='clarity-cookie-title' data-clarity-mask='true'>
            <Box className={classes.header}>
              <Box className={classes.icon}><PrivacyIcon aria-hidden='true' /></Box>
              <Box className={classes.heading}>
                <Typography className={classes.eyebrow}>{t('eyebrow')}</Typography>
                <Typography id='clarity-cookie-title' className={`${classes.title} ${classes.bannerTitle}`} component='h2'>{t('title')}</Typography>
              </Box>
              <IconButton className={classes.close} onClick={() => choose(false)} aria-label={t('rejectLabel')}><span aria-hidden='true'>×</span></IconButton>
            </Box>
            <Typography className={`${classes.copy} ${classes.bannerCopy}`}>
              <Trans t={t} i18nKey='description' components={{ provider: <strong />, support: <strong /> }} />
            </Typography>
            <Box className={classes.bannerNote}><InfoIcon aria-hidden='true' /><Typography>{t('choiceNote')}</Typography></Box>
            {errorMessage}
            <Box className={classes.actions}>
              <Button className={`${classes.button} ${classes.bannerButton} ${classes.bannerSecondary}`} variant='outlined' onClick={() => choose(false)}>{t('reject')}</Button>
              <Button className={`${classes.button} ${classes.bannerButton} ${classes.primary}`} variant='contained' onClick={() => choose(true)}>{t('accept')}</Button>
            </Box>
            <Box className={classes.bannerFooter}>
              <Button className={classes.customize} onClick={showPreferences}>{t('preferences')}</Button>
              {privacyLink}
            </Box>
          </Paper>
        ) : !open ? (
          <Button className={classes.preferences} variant='outlined' size='small' aria-label={t('reopen')} startIcon={<span aria-hidden='true'>🛡</span>} onClick={showBanner}>
            {t('reopen')}
          </Button>
        ) : null}
      </Portal>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth={false} fullWidth
        aria-labelledby='clarity-preferences-title' aria-describedby='clarity-preferences-description'
        BackdropProps={{ style: { backgroundColor: 'rgba(15, 23, 42, 0.32)' } }}
        PaperProps={{ className: classes.dialog, 'data-clarity-mask': 'true' }}>
        <DialogTitle className={classes.dialogHeader} disableTypography>
          <Box className={classes.header}>
            <Box className={classes.icon}><PrivacyIcon aria-hidden='true' /></Box>
            <Box className={classes.heading}>
              <Typography className={classes.eyebrow}>{t('eyebrow')}</Typography>
              <Typography id='clarity-preferences-title' className={classes.title} component='h2'>{t('preferencesTitle')}</Typography>
            </Box>
            <IconButton className={classes.close} onClick={() => setOpen(false)} aria-label={t('close')}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogBody}>
          <Typography id='clarity-preferences-description' className={classes.copy}>{t('preferencesDescription')}</Typography>
          <Box className={classes.option}>
            <Box className={classes.optionCopy}>
              <Typography id='clarity-analytics-label' className={classes.optionTitle} component='label' htmlFor='clarity-analytics-control'>{t('analytics')}</Typography>
              <Typography className={classes.optional}>{t('optional')}</Typography>
              <Typography id='clarity-analytics-description' className={`${classes.copy} ${classes.optionDescription}`}>{t('analyticsDescription')}</Typography>
            </Box>
            <Switch className={classes.switch} checked={analytics} onChange={event => setAnalytics(event.target.checked)} color='primary'
              inputProps={{ id: 'clarity-analytics-control', 'aria-labelledby': 'clarity-analytics-label', 'aria-describedby': 'clarity-analytics-description' }} />
          </Box>
          {revoking && <Box className={classes.notice} role='status'><InfoIcon aria-hidden='true' /><Typography>{t('reloadWarning')}</Typography></Box>}
          <Box className={classes.dialogPrivacy}>{privacyLink}</Box>
          {errorMessage}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button className={`${classes.button} ${classes.choice}`} variant='outlined' onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button className={`${classes.button} ${classes.primary}`} variant='contained' color='primary' disabled={Boolean(consent && consent.analytics === analytics)} onClick={() => choose(analytics)}>
            {t(revoking ? 'revoke' : 'save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Clarity
